import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { prayersAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Heart, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const emptyForm = { title: '', content: '', image_url: '' };

export default function PrayersPage() {
  const { selectedChurchId, canCreate, canDelete } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const pg = usePagination(String(selectedChurchId));

  const fetchItems = useCallback(() => {
    setLoading(true);
    prayersAPI.list(pg.params)
      .then(res => { setItems(res.data?.data || []); setTotal(res.data?.total || 0); })
      .catch(err => toast.error(hubError(err, 'Error al cargar oraciones')))
      .finally(() => setLoading(false));
  }, [pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setForm(emptyForm); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.title) { toast.error('Título requerido'); return; }
    setSaving(true);
    try {
      await prayersAPI.create({ ...form, image_url: form.image_url || null });
      toast.success('Oración publicada');
      setDialogOpen(false); fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await prayersAPI.delete(id); toast.success('Oración eliminada'); fetchItems(); }
    catch (err) { toast.error(hubError(err, 'Error al eliminar')); }
  };

  return (
    <div>
      <TopBar title="Oraciones" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('prayers') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="prayers-create-button"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="oraciones" testId="prayers-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><Heart className="w-12 h-12 text-muted-foreground/40" /><p>No hay oraciones</p>{canCreate('prayers') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear oración</Button>}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <Card key={item.id} className="stat-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#D64545]/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#D64545]" />
                    </div>
                    {canDelete('prayers') && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`prayer-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>¿Eliminar oración?</AlertDialogTitle><AlertDialogDescription>No se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm mb-2">{item.title}</h3>
                  {item.content && <p className="text-xs text-muted-foreground line-clamp-4">{item.content}</p>}
                  {item.created_at && <p className="text-[10px] text-muted-foreground mt-3">{new Date(item.created_at).toLocaleDateString('es-ES')}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Nueva Oración</DialogTitle><DialogDescription className="sr-only">Formulario de oración</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Título *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="prayer-title-input" /></div>
            <div className="space-y-2"><Label>Contenido</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={6} data-testid="prayer-content-input" /></div>
            <div className="space-y-2"><Label>Imagen (URL)</Label><Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." data-testid="prayer-image-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="prayer-save-button">{saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}Publicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
